import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { IoMdContact } from "react-icons/io";
import { IoCall } from "react-icons/io5";
import { FaAddressBook } from "react-icons/fa";
import { FaHourglassStart } from "react-icons/fa";
import { SiStatuspage } from "react-icons/si";
import { MdReport } from "react-icons/md";
import { PiBagSimpleFill } from "react-icons/pi";
import { FaClock } from "react-icons/fa";
import { FaBuilding } from "react-icons/fa";
import { FaCalendarAlt } from "react-icons/fa";
import { getAuthHeaders } from "../../utils/auth";
import {
  formatUsDateFromIso,
  formatUsDateTimeFromIso,
} from "../../utils/usDateFormat";
import { formatCheckInOutDurationFromRecord } from "../../utils/attendanceMetrics";
import {
  defaultAttendanceDateRange,
  defaultCustomAttendanceRange,
  buildAttendanceHistoryDateParams,
  canFetchWithAttendanceDateFilter,
  DEFAULT_ATTENDANCE_DAYS,
  ATTENDANCE_DAYS_FILTER_CUSTOM,
  formatAttendanceHistorySummary,
} from "../../utils/attendanceDateRange";
import AttendanceDateRangeFilter from "../AttendanceDateRangeFilter/AttendanceDateRangeFilter";
import AttendanceDaysFilter from "../AttendanceDaysFilter/AttendanceDaysFilter";
import Pagination from "../Pagination/Pagination";
import { getEmployeeProfileImage } from "../../utils/employeeProfileImage";

const ATTENDANCE_PAGE_SIZE = 100;

const hasAdminPrivileges = (role) => {
  return role === 'admin' || role === 'super';
};

const getAttendanceStatusLabel = (statusValue) => {
  if (statusValue === 'checked_in') return 'Checked In';
  if (statusValue === 'absent' || statusValue === 'leave') return 'Absent';
  return 'Checked Out';
};

const getAttendanceStatusClass = (statusValue) => {
  if (statusValue === 'checked_in') return 'bg-green-100 text-green-700';
  if (statusValue === 'absent' || statusValue === 'leave') return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
};

/** Stored values match EmployeeStatus; Exited is shown as Inactive in the UI. */
const EMPLOYEE_STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Exited', label: 'Inactive' },
  { value: 'On Leave', label: 'On Leave' },
];

const formatEmployeeStatusLabel = (status) => {
  if (!status) return 'N/A';
  if (status === 'Exited' || status === 'Inactive') return 'Inactive';
  return status;
};

const normalizeEmployeeStatusValue = (status) => {
  if (status === 'Inactive') return 'Exited';
  if (status === 'Active' || status === 'On Leave' || status === 'Exited') {
    return status;
  }
  return 'Active';
};

const NEW_GROUP_LABELS = {
  '100000000': 'Staff',
  '100000001': 'Management',
  '100000002': 'BOH',
  '100000003': 'FOH',
  '1000000000': 'Staff',
  '1000000001': 'Management',
  '1000000002': 'BOH',
  '1000000003': 'FOH'
};

/** Avoid showing raw Dynamics lookup GUIDs when sync has not yet populated *_name fields */
const GUID_LIKE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isGuidLike(value) {
  if (value == null || value === '') return false;
  return typeof value === 'string' && GUID_LIKE.test(value.trim());
}

function firstNonGuid(...candidates) {
  for (const v of candidates) {
    if (v == null || v === '') continue;
    if (!isGuidLike(String(v))) return v;
  }
  return null;
}

export default function EmployeeDetails({
  employee,
  onBack,
  userProfile,
  handleEditEmployee,
  formatCurrency,
  getRoleDisplayName,
  getRoleColor,
  getGroupColor,
  getStatusColor,
  currentView,
  openAttendanceRecord,
  onAttendanceNotificationHandled,
  canDeleteEmployee = false,
  onDeleteEmployee,
  onEmployeeUpdated,
}) {
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [attendanceError, setAttendanceError] = useState(null);
  const defaultAttendanceRange = defaultAttendanceDateRange();
  const [attendanceStartDate, setAttendanceStartDate] = useState(
    defaultAttendanceRange.startDate
  );
  const [attendanceEndDate, setAttendanceEndDate] = useState(
    defaultAttendanceRange.endDate
  );
  const [attendanceDays, setAttendanceDays] = useState(DEFAULT_ATTENDANCE_DAYS);
  const [attendanceFilterMode, setAttendanceFilterMode] = useState('days');
  const [attendancePage, setAttendancePage] = useState(1);
  const [attendanceTotalPages, setAttendanceTotalPages] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [faceReference, setFaceReference] = useState({
    enrolled: Boolean(employee?.checkin_image || employee?.checkin_face?.image_url),
    image_url: employee?.checkin_image || employee?.checkin_face?.image_url || null,
  });
  const [faceUploadFile, setFaceUploadFile] = useState(null);
  const [faceUploadLoading, setFaceUploadLoading] = useState(false);
  const [faceUploadMessage, setFaceUploadMessage] = useState('');
  const [faceUploadError, setFaceUploadError] = useState('');
  const [profileImage, setProfileImage] = useState(() => getEmployeeProfileImage(employee));
  const [shiftHistory, setShiftHistory] = useState([]);
  const [shiftHistoryLoading, setShiftHistoryLoading] = useState(false);
  const [shiftHistoryError, setShiftHistoryError] = useState(null);
  const [shiftHistoryPage, setShiftHistoryPage] = useState(1);
  const [shiftHistoryTotalPages, setShiftHistoryTotalPages] = useState(0);
  const [shiftOptions, setShiftOptions] = useState([]);
  const handledAttendanceNotificationRef = useRef(null);
  const [defaultShiftName, setDefaultShiftName] = useState('');
  const [selectedShiftId, setSelectedShiftId] = useState('');
  const [shiftUpdateLoading, setShiftUpdateLoading] = useState(false);
  const [shiftUpdateMessage, setShiftUpdateMessage] = useState('');
  const [shiftUpdateError, setShiftUpdateError] = useState('');
  const [employeeStatus, setEmployeeStatus] = useState(
    normalizeEmployeeStatusValue(employee?.status)
  );
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState('');
  const [statusUpdateMessage, setStatusUpdateMessage] = useState('');
  const employeeIdentifier = employee?.id || employee?.employee_id;
  const isSuper = userProfile?.role === "super";

  const formatShiftHistoryDateTime = (iso) =>
    formatUsDateTimeFromIso(iso) || "—";

  const fetchShiftHistory = async (page = 1) => {
    const empId = employee?.id || employee?.employee_id;
    if (!empId || !isSuper) return;
    try {
      setShiftHistoryLoading(true);
      setShiftHistoryError(null);
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/employees/${empId}/shift-history`,
        { headers, params: { page, page_size: 10 } }
      );
      setShiftHistory(response.data.history || []);
      setShiftHistoryTotalPages(response.data.total_pages || 0);
      setShiftHistoryPage(page);
    } catch (error) {
      console.error("Error fetching shift history:", error);
      setShiftHistoryError(
        error.response?.data?.detail || "Failed to load shift change history."
      );
      setShiftHistory([]);
    } finally {
      setShiftHistoryLoading(false);
    }
  };

  const fetchShiftOptions = async () => {
    if (!isSuper) return;
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/shifts/options`,
        { headers }
      );
      setShiftOptions(response.data.shifts || []);
      setDefaultShiftName(response.data.default_shift_name || '');
    } catch (error) {
      console.error('Error fetching shift options:', error);
      setShiftOptions([]);
      setDefaultShiftName('');
    }
  };

  const handleUpdateShift = async () => {
    if (!employeeIdentifier) return;
    try {
      setShiftUpdateLoading(true);
      setShiftUpdateError('');
      setShiftUpdateMessage('');
      const headers = await getAuthHeaders();
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/employees/${employeeIdentifier}`,
        { shift_id: selectedShiftId || null },
        { headers }
      );
      setShiftUpdateMessage('Shift updated successfully.');
      await fetchShiftHistory(1);
    } catch (error) {
      console.error('Error updating shift:', error);
      setShiftUpdateError(
        error?.response?.data?.detail || 'Failed to update shift.'
      );
    } finally {
      setShiftUpdateLoading(false);
    }
  };

  const handleStatusChange = async (event) => {
    const nextStatus = normalizeEmployeeStatusValue(event.target.value);
    if (!employeeIdentifier || nextStatus === employeeStatus) return;

    const previousStatus = employeeStatus;
    setEmployeeStatus(nextStatus);
    setStatusUpdateLoading(true);
    setStatusUpdateError('');
    setStatusUpdateMessage('');

    try {
      const headers = await getAuthHeaders();
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/employees/${employeeIdentifier}/status`,
        { status: nextStatus },
        { headers }
      );
      setStatusUpdateMessage('Status updated successfully.');
      onEmployeeUpdated?.({ ...employee, status: nextStatus });
    } catch (error) {
      console.error('Error updating employee status:', error);
      setEmployeeStatus(previousStatus);
      setStatusUpdateError(
        error?.response?.data?.detail || 'Failed to update status.'
      );
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const fetchAttendanceHistory = async (page = 1) => {
    if (!employee?.id) return;
    const rangeStart =
      attendanceFilterMode === ATTENDANCE_DAYS_FILTER_CUSTOM ? attendanceStartDate : '';
    const rangeEnd =
      attendanceFilterMode === ATTENDANCE_DAYS_FILTER_CUSTOM ? attendanceEndDate : '';
    if (
      attendanceFilterMode === ATTENDANCE_DAYS_FILTER_CUSTOM &&
      !canFetchWithAttendanceDateFilter(rangeStart, rangeEnd)
    ) {
      return;
    }

    try {
      setLoadingAttendance(true);
      setAttendanceError(null);

      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/attendance/history`,
        {
          headers,
          params: {
            selected_employee_id: employee.id,
            ...buildAttendanceHistoryDateParams(
              rangeStart,
              rangeEnd,
              attendanceDays
            ),
            page,
            page_size: ATTENDANCE_PAGE_SIZE,
          }
        }
      );

      if (response.data.success && Array.isArray(response.data.records)) {
        setAttendanceHistory(response.data.records);
        setAttendanceTotalPages(response.data.total_pages || 0);
        setAttendancePage(response.data.page || page);
      } else {
        setAttendanceHistory([]);
        setAttendanceTotalPages(0);
      }
    } catch (error) {
      console.error('Error fetching attendance history:', error);
      setAttendanceError('Failed to load attendance history');
      setAttendanceHistory([]);
      setAttendanceTotalPages(0);
    } finally {
      setLoadingAttendance(false);
    }
  };

  const openImageModal = (record) => {
    setSelectedRecord(record);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedRecord(null);
  };

  const handleFaceUpload = async () => {
    if (!employeeIdentifier || !faceUploadFile) return;
    try {
      setFaceUploadLoading(true);
      setFaceUploadError('');
      setFaceUploadMessage('');

      const headers = await getAuthHeaders();
      const formData = new FormData();
      formData.append('image', faceUploadFile);

      const uploadHeaders = { ...headers };
      delete uploadHeaders['Content-Type'];

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/employees/${employeeIdentifier}/checkin-face`,
        formData,
        { headers: uploadHeaders }
      );

      setFaceReference(response.data?.checkin_face || { enrolled: true });
      setProfileImage(
        response.data?.profile_image ||
          response.data?.checkin_face?.image_url ||
          null
      );
      setFaceUploadFile(null);
      setFaceUploadMessage('Face image uploaded and set as profile photo.');
    } catch (error) {
      console.error('Error uploading check-in face image:', error);
      setFaceUploadError(error?.response?.data?.detail || 'Failed to upload check-in face image.');
    } finally {
      setFaceUploadLoading(false);
    }
  };

  const handleFaceReferenceDelete = async () => {
    if (!employeeIdentifier) return;
    try {
      setFaceUploadLoading(true);
      setFaceUploadError('');
      setFaceUploadMessage('');

      const headers = await getAuthHeaders();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/employees/${employeeIdentifier}/checkin-face`,
        { headers }
      );

      setFaceReference({ enrolled: false, image_url: null });
      setProfileImage((current) =>
        current && current === faceReference?.image_url ? null : current
      );
      setFaceUploadFile(null);
      setFaceUploadMessage('Check-in face enrollment removed successfully.');
    } catch (error) {
      console.error('Error removing check-in face enrollment:', error);
      setFaceUploadError(error?.response?.data?.detail || 'Failed to remove check-in face enrollment.');
    } finally {
      setFaceUploadLoading(false);
    }
  };

  useEffect(() => {
    setAttendancePage(1);
  }, [employee?.id]);

  useEffect(() => {
    // Only fetch attendance history when viewing from My Teams tab
    if (employee?.id && currentView !== "profile") {
      fetchAttendanceHistory(attendancePage);
    }
  }, [
    employee?.id,
    currentView,
    attendancePage,
    attendanceStartDate,
    attendanceEndDate,
    attendanceDays,
    attendanceFilterMode,
  ]);

  useEffect(() => {
    if (!openAttendanceRecord?.id) {
      return;
    }
    if (handledAttendanceNotificationRef.current === openAttendanceRecord.id) {
      return;
    }
    if (loadingAttendance) {
      return;
    }

    const recordInHistory = attendanceHistory.find(
      (record) => record.id === openAttendanceRecord.id
    );
    const recordToOpen = recordInHistory || openAttendanceRecord;
    openImageModal(recordToOpen);
    handledAttendanceNotificationRef.current = openAttendanceRecord.id;
    onAttendanceNotificationHandled?.();
  }, [
    openAttendanceRecord,
    attendanceHistory,
    loadingAttendance,
    onAttendanceNotificationHandled,
  ]);

  useEffect(() => {
    if (isSuper && (employee?.id || employee?.employee_id)) {
      fetchShiftHistory(1);
      fetchShiftOptions();
    } else {
      setShiftHistory([]);
      setShiftHistoryPage(1);
      setShiftHistoryTotalPages(0);
      setShiftOptions([]);
      setDefaultShiftName('');
    }
  }, [employee?.id, employee?.employee_id, isSuper]);

  useEffect(() => {
    setSelectedShiftId(employee?.shift_id || '');
    setShiftUpdateMessage('');
    setShiftUpdateError('');
  }, [employee?.id, employee?.employee_id, employee?.shift_id]);

  useEffect(() => {
    setEmployeeStatus(normalizeEmployeeStatusValue(employee?.status));
    setStatusUpdateMessage('');
    setStatusUpdateError('');
  }, [employee?.id, employee?.employee_id, employee?.status]);

  useEffect(() => {
    setFaceReference({
      enrolled: Boolean(employee?.checkin_image || employee?.checkin_face?.image_url),
      image_url: employee?.checkin_image || employee?.checkin_face?.image_url || null,
    });
    setProfileImage(getEmployeeProfileImage(employee));
    setFaceUploadFile(null);
    setFaceUploadMessage('');
    setFaceUploadError('');
  }, [employee?.id, employee?.employee_id, employee?.checkin_image, employee?.checkin_face?.image_url, employee?.image]);

  if (!employee) return null;

  const canChangeEmployeeStatus =
    hasAdminPrivileges(userProfile?.role) && currentView !== 'profile';

  const isCustomAttendanceFilter = attendanceFilterMode === ATTENDANCE_DAYS_FILTER_CUSTOM;
  const attendanceFilterSummary = formatAttendanceHistorySummary(
    attendanceDays,
    isCustomAttendanceFilter ? attendanceStartDate : '',
    isCustomAttendanceFilter ? attendanceEndDate : '',
    formatUsDateFromIso
  );

  const handleAttendanceFilterModeChange = (mode) => {
    setAttendanceFilterMode(mode);
    setAttendancePage(1);
    if (mode === ATTENDANCE_DAYS_FILTER_CUSTOM) {
      const customRange = defaultCustomAttendanceRange();
      setAttendanceStartDate(customRange.startDate);
      setAttendanceEndDate(customRange.endDate);
    } else {
      setAttendanceStartDate('');
      setAttendanceEndDate('');
    }
  };

  const handleAttendanceStartChange = (iso) => {
    setAttendanceStartDate(iso);
    setAttendancePage(1);
  };

  const handleAttendanceEndChange = (iso) => {
    setAttendanceEndDate(iso);
    setAttendancePage(1);
  };

  const rawDob = employee.new_dob ?? employee.dateOfBirth;
  const displayDob = rawDob ? formatUsDateFromIso(rawDob) || '-' : '-';
  const rawNewGroup = employee.new_group;
  const normalizedNewGroup = rawNewGroup === 'Manager' ? 'Management' : rawNewGroup;
  const resolvedNewGroup = /^\d+$/.test(String(normalizedNewGroup ?? ''))
    ? NEW_GROUP_LABELS[String(normalizedNewGroup)] ?? null
    : normalizedNewGroup;
  const fallbackGroup = employee.group === 'Manager' ? 'Management' : employee.group;
  const displayGroup = resolvedNewGroup ?? fallbackGroup ?? '-';
  const displayContact = employee.seazen_mobile ?? employee.phone ?? '-';
  const displayLocation =
    firstNonGuid(
      employee.new_location_name,
      employee.location,
      employee.new_location,
      employee.location_id
    ) ?? 'Kuwait';
  const displayLeaveBalance = employee.new_leavebalance ?? employee.leave_balance ?? 0;
  const displayCompany =
    firstNonGuid(
      employee.new_company_name,
      employee.company,
      employee.new_company
    ) ?? '-';
  const displayEntity =
    firstNonGuid(
      employee.new_entity_name,
      employee.entity,
      employee.new_entity
    ) ?? '-';

  return (
    <div className="mx-auto w-full min-w-0 max-w-full bg-white border rounded-lg shadow overflow-x-hidden">
      {(userProfile?.role !== 'staff' && currentView !== "profile") && <button
        onClick={onBack}
        className="text-[#674936] pt-4 pl-4 hover:text-[#1a365d] text-sm hover:underline"
      >
        ← Go Back
      </button>
      }
      <div className='p-4'>
        {/* Profile Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <h2 className='text-2xl font-semibold'>Profile</h2>
          <div className="flex flex-wrap items-center justify-end gap-2 ml-auto">
            {canDeleteEmployee && userProfile?.role !== 'staff' && (
              <button
                type="button"
                onClick={() => onDeleteEmployee?.(employee)}
                className="bg-[#fed7d7] text-[#e53e3e] px-4 py-2 rounded-lg hover:bg-[#ffb3b3] transition-colors duration-300 font-semibold text-sm"
              >
                Delete
              </button>
            )}
            {canChangeEmployeeStatus && (
              <div className="flex flex-col items-end gap-1">
                <label htmlFor="employee-status-select" className="sr-only">
                  Employee status
                </label>
                <select
                  id="employee-status-select"
                  value={employeeStatus}
                  onChange={handleStatusChange}
                  disabled={statusUpdateLoading}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-semibold text-gray-800 bg-white focus:outline-none focus:border-[#674936] disabled:opacity-50 disabled:cursor-not-allowed min-w-[9rem]"
                  aria-label="Change employee status"
                >
                  {EMPLOYEE_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
        {(statusUpdateError || statusUpdateMessage) && canChangeEmployeeStatus && (
          <div className="mb-3">
            {statusUpdateError && (
              <p className="text-sm text-red-600">{statusUpdateError}</p>
            )}
            {statusUpdateMessage && !statusUpdateError && (
              <p className="text-sm text-green-700">{statusUpdateMessage}</p>
            )}
          </div>
        )}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-6 min-w-0">
          {profileImage ? (
            <img
              src={profileImage}
              alt={employee.name}
              className="w-16 h-16 rounded-full object-cover"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-lg font-bold text-gray-600">
              {employee.name
                .split(' ')
                .map((n) => n[0])
                .join('')
                .slice(0, 2)
                .toUpperCase() ?? '-'}
            </div>
          )}
          <div className="sm:text-left min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-500">Name: <span className="text-md font-semibold text-gray-900 break-words">{employee.name ?? '-'}</span></div>
            <div className="text-sm font-semibold text-gray-500">Email: <span className="text-md font-semibold text-gray-900 break-all">{employee.email ?? '-'}</span></div>
            <div className="text-sm font-semibold text-gray-500">DOB: <span className="text-md font-semibold text-gray-900">{displayDob}</span></div>
          </div>
        </div>

        {/* Status, Group, ID */}
        <div className="flex flex-wrap gap-4 items-center text-sm">
          <span className={`status ${getStatusColor(employeeStatus)}`}>
            {formatEmployeeStatusLabel(employeeStatus)}
          </span>
          {displayGroup && (
            <span className={`group-badge ${getGroupColor(displayGroup)}`}>
              {displayGroup}
            </span>
          )}
          <span className="employee-number">
            <strong>ID:</strong> {employee.employee_no ?? 'N/A'}
          </span>
        </div>
      </div>

      {hasAdminPrivileges(userProfile?.role) && (
        <div className="px-4 pb-4">
          <div className="py-1 px-4 font-semibold w-full bg-gray-100 rounded-t">Face Enrollment</div>
          <div className="border border-t-0 rounded-b p-4">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-sm font-semibold text-gray-600">Enrollment Status:</span>
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                  faceReference?.enrolled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {faceReference?.enrolled ? 'Enrolled' : 'Not Enrolled'}
              </span>
            </div>

            {faceReference?.image_url && (
              <div className="mb-3">
                <p className="text-sm font-semibold text-gray-600 mb-2">Current check-in face image:</p>
                <img
                  src={faceReference.image_url}
                  alt="Check-in face enrollment"
                  className="w-24 h-24 object-cover rounded border"
                />
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 mb-3">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFaceUploadFile(e.target.files?.[0] || null)}
                className="text-sm"
              />
              <button
                onClick={handleFaceUpload}
                disabled={!faceUploadFile || faceUploadLoading}
                className={`px-4 py-2 rounded text-white text-sm font-semibold ${
                  !faceUploadFile || faceUploadLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-[#674936] hover:bg-[#503220]'
                }`}
              >
                {faceUploadLoading ? 'Saving...' : (faceReference?.enrolled ? 'Replace Face Image' : 'Upload Face Image')}
              </button>
              {faceReference?.enrolled && (
                <button
                  onClick={handleFaceReferenceDelete}
                  disabled={faceUploadLoading}
                  className={`px-4 py-2 rounded text-sm font-semibold border ${
                    faceUploadLoading
                      ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                      : 'border-red-300 text-red-600 hover:bg-red-50'
                  }`}
                >
                  Remove
                </button>
              )}
            </div>

            {faceUploadMessage && <p className="text-sm text-green-600">{faceUploadMessage}</p>}
            {faceUploadError && <p className="text-sm text-red-600">{faceUploadError}</p>}
            <p className="text-xs text-gray-500 mt-2">
              This reference image is used to verify employee identity during check-in and check-out.
              This image is also used as the employee profile photo.
            </p>
          </div>
        </div>
      )}

      {/* Grid Layout for Basic Info and Job Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
        {/* Basic Info Section */}
        <div>
          <div className='py-1 px-4 font-semibold w-full bg-gray-100'>Basic Info</div>
          <div className="min-h-[100px] pt-3">
            <div className="flex flex-wrap gap-2 items-start px-4 pb-3 min-w-0">
              <IoMdContact className='text-2xl shrink-0'/>
              <span className="text-sm font-semibold text-gray-500 shrink-0">Contact Number:</span>
              <span className='text-md font-semibold break-words min-w-0'>{displayContact}</span>
            </div>
            <div className="flex flex-wrap gap-2 items-start px-4 pb-3 min-w-0">
              <IoCall className='text-xl shrink-0'/>
              <span className="text-sm font-semibold text-gray-500 shrink-0">Emergency Number:</span>
              <span className='text-md font-semibold break-words min-w-0'>{employee.phone ?? '-'}</span>
            </div>
            <div className="flex flex-wrap gap-2 items-start px-4 pb-3 min-w-0">
              <FaAddressBook className='text-xl shrink-0'/>
              <span className="text-sm font-semibold text-gray-500 shrink-0">Address:</span>
              <span className='text-md font-semibold break-words min-w-0'>{displayLocation}</span>
            </div>
            <div className="flex flex-wrap gap-2 items-start px-4 pb-3 min-w-0">
              <FaCalendarAlt className='text-xl shrink-0'/>
              <span className="text-sm font-semibold text-gray-500 shrink-0">Leave Balance:</span>
              <span className='text-md font-semibold break-words min-w-0'>
                {displayLeaveBalance}
              </span>
            </div>
          </div>
        </div>

        {/* Job Info Section */}
        <div>
          <div className='py-1 px-4 font-semibold w-full bg-gray-100'>Job Info</div>
          <div className='min-h-[100px] pt-3'>
            <div className="flex flex-wrap gap-2 items-start px-4 pb-2 min-w-0">
              <PiBagSimpleFill className='text-2xl shrink-0'/>
              <span className="text-sm font-semibold text-gray-500 shrink-0">Position:</span>
              <span className='text-md font-semibold break-words min-w-0'>{employee.position ?? '-'}</span>
            </div>
            <div className="flex flex-wrap gap-2 items-start px-4 pb-2 min-w-0">
              <PiBagSimpleFill className='text-2xl shrink-0'/>
              <span className="text-sm font-semibold text-gray-500 shrink-0">Grade:</span>
              <span className='text-md font-semibold break-words min-w-0'>
                {employee.grade_name && employee.grade_name !== '—'
                  ? employee.grade_name
                  : '—'}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 items-start px-4 pb-2 min-w-0">
              <FaBuilding className='text-xl shrink-0'/>
              <span className="text-sm font-semibold text-gray-500 shrink-0">Department:</span>
              <span className='text-md font-semibold break-words min-w-0'>{employee.department ?? '-'}</span>
            </div>
            <div className="flex flex-wrap gap-2 items-start px-4 pb-2 min-w-0">
              <FaBuilding className='text-xl shrink-0'/>
              <span className="text-sm font-semibold text-gray-500 shrink-0">Company:</span>
              <span className='text-md font-semibold break-words min-w-0'>{displayCompany}</span>
            </div>
            <div className="flex flex-wrap gap-2 items-start px-4 pb-2 min-w-0">
              <FaBuilding className='text-xl shrink-0'/>
              <span className="text-sm font-semibold text-gray-500 shrink-0">Entity:</span>
              <span className='text-md font-semibold break-words min-w-0'>{displayEntity}</span>
            </div>
            <div className="flex flex-wrap gap-2 items-start px-4 pb-2 min-w-0">
              <SiStatuspage className='text-xl shrink-0'/>
              <span className="text-sm font-semibold text-gray-500 shrink-0">Status:</span>
              <span className='text-md font-semibold break-words min-w-0'>{'Full-Time'}</span>
            </div>
            <div className="flex flex-wrap gap-2 items-start px-4 pb-2 min-w-0">
              <FaHourglassStart className='text-xl shrink-0'/>
              <span className="text-sm font-semibold text-gray-500 shrink-0">Start Day:</span>
              <span className='text-md font-semibold break-words min-w-0'>{employee.joining_date ? formatUsDateFromIso(employee.joining_date) || '-' : '-'}</span>
            </div>
            <div className="flex flex-wrap gap-2 items-start px-4 pb-2 min-w-0">
              <MdReport className='text-2xl shrink-0'/>
              <span className="text-sm font-semibold text-gray-500 shrink-0">Report Hierarchy:</span>
              <span className='text-md font-semibold break-all min-w-0'>{employee.reporting_to ?? '-'}</span>
            </div>
            {(hasAdminPrivileges(userProfile?.role) || userProfile?.role === 'manager') && employee.salary != null && (
              <div className="flex flex-wrap gap-2 items-start px-4 pb-2 min-w-0">
                <span className="text-sm font-semibold text-gray-500 shrink-0">Salary:</span>
                <span className="detail-value salary text-md font-semibold break-words min-w-0">
                  {(employee.salary_formatted || formatCurrency(employee.salary)) ?? '-'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Attendance History Section - Only show when viewing from My Teams, not from My Profile */}
      {currentView !== "profile" && userProfile.role !== "staff" && (
        <>
          <div className='py-1 px-4 font-semibold w-full bg-gray-100 flex items-center gap-2'>
            <FaClock className='text-lg' />
            Check-in & Check-out History
          </div>
          <div className="p-4">
            <div className="mb-4 flex flex-wrap items-end gap-4">
              <AttendanceDaysFilter
                id="employee-attendance-days"
                compact
                mode={attendanceFilterMode}
                days={attendanceDays}
                onModeChange={handleAttendanceFilterModeChange}
                onDaysChange={(value) => {
                  setAttendanceDays(value);
                  setAttendancePage(1);
                }}
              />
              {isCustomAttendanceFilter && (
                <AttendanceDateRangeFilter
                  idPrefix="employee-attendance"
                  compact
                  startDate={attendanceStartDate}
                  endDate={attendanceEndDate}
                  onStartChange={handleAttendanceStartChange}
                  onEndChange={handleAttendanceEndChange}
                />
              )}
              <p className="text-sm text-gray-600 pb-2 w-full sm:w-auto">
                {attendanceFilterSummary}
              </p>
            </div>
            {loadingAttendance ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#674936]"></div>
                <span className="ml-2 text-gray-600">Loading attendance history...</span>
              </div>
            ) : attendanceError ? (
              <div className="flex justify-center items-center py-8 text-red-600">
                <span>{attendanceError}</span>
              </div>
            ) : attendanceHistory.length === 0 ? (
              <div className="flex justify-center items-center py-8 text-gray-500">
                <span>No attendance records found.</span>
              </div>
            ) : (
              <div className="relative overflow-x-auto border border-gray-200 rounded-lg w-full shadow-sm">
                <table className="min-w-full divide-y divide-gray-200 text-sm text-left">
                  <thead className="bg-[#674936]">
                    <tr>
                      <th className="px-4 py-3 text-white font-semibold">Location</th>
                      <th className="px-4 py-3 text-white font-semibold">Check In - Check Out</th>
                      <th className="px-4 py-3 text-white font-semibold">Date</th>
                      <th className="px-4 py-3 text-white font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {attendanceHistory.map((record) => {
                      const hasDetails =
                        record.image_url ||
                        record.image_url_checkout ||
                        record.check_in_no_location_reason ||
                        record.check_out_no_location_reason;
                      return (
                      <tr 
                        key={record.id} 
                        className={`bg-white hover:bg-gray-50${hasDetails ? ' cursor-pointer' : ''}`}
                        onClick={() => {
                          if (hasDetails) {
                            openImageModal(record);
                          }
                        }}
                      >
                        <td className="px-4 py-3">
                          <span className="text-[#674936] hover:text-[#503220] font-medium hover:underline">
                            {record.location_name}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {formatCheckInOutDurationFromRecord(record)}
                        </td>
                        <td className="px-4 py-3">{formatUsDateFromIso(record.work_date) || record.work_date || '-'}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getAttendanceStatusClass(
                              record.status
                            )}`}
                          >
                            <span className="w-2 h-2 mr-1 rounded-full bg-current"></span>
                            {getAttendanceStatusLabel(record.status)}
                          </span>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {attendanceTotalPages > 1 && (
              <div className="mt-4 flex justify-center">
                <Pagination
                  totalPages={attendanceTotalPages}
                  currentPage={attendancePage}
                  onPageChange={(page) => setAttendancePage(page)}
                />
              </div>
            )}
          </div>
        </>
      )}

      {isSuper && (
        <>
          <div className="py-1 px-4 font-semibold w-full bg-gray-100 flex items-center gap-2">
            <FaClock className="text-lg" />
            Update shift
          </div>
          <div className="p-4 border-b border-gray-100">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[250px] flex-1">
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Assigned shift
                </label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={selectedShiftId}
                  onChange={(e) => setSelectedShiftId(e.target.value)}
                  disabled={shiftUpdateLoading}
                >
                  <option value="">
                    Default{defaultShiftName ? ` (${defaultShiftName})` : ''}
                  </option>
                  {shiftOptions.map((shift) => (
                    <option key={shift.id} value={shift.id}>
                      {shift.name}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={handleUpdateShift}
                disabled={shiftUpdateLoading}
                className={`px-4 py-2 rounded text-white text-sm font-semibold ${
                  shiftUpdateLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-[#674936] hover:bg-[#503220]'
                }`}
              >
                {shiftUpdateLoading ? 'Updating...' : 'Update Shift'}
              </button>
            </div>
            {shiftUpdateMessage && (
              <p className="text-sm text-green-600 mt-2">{shiftUpdateMessage}</p>
            )}
            {shiftUpdateError && (
              <p className="text-sm text-red-600 mt-2">{shiftUpdateError}</p>
            )}
          </div>

          <div className="py-1 px-4 font-semibold w-full bg-gray-100 flex items-center gap-2">
            <FaClock className="text-lg" />
            Shift change history
          </div>
          <div className="p-4">
            {employee.shift_name && (
              <p className="text-sm text-gray-600 mb-3">
                Current shift:{" "}
                <span className="font-semibold text-gray-800">
                  {employee.shift_name}
                  {employee.shift_is_default && (
                    <span className="text-gray-500 font-normal"> (default)</span>
                  )}
                </span>
              </p>
            )}
            {shiftHistoryLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#674936]"></div>
                <span className="ml-2 text-gray-600">Loading shift history...</span>
              </div>
            ) : shiftHistoryError ? (
              <div className="flex justify-center items-center py-8 text-red-600">
                <span>{shiftHistoryError}</span>
              </div>
            ) : shiftHistory.length === 0 ? (
              <div className="flex justify-center items-center py-8 text-gray-500">
                <span>No shift changes recorded.</span>
              </div>
            ) : (
              <>
                <div className="relative overflow-x-auto border border-gray-200 rounded-lg w-full shadow-sm">
                  <table className="min-w-full divide-y divide-gray-200 text-sm text-left">
                    <thead className="bg-[#674936]">
                      <tr>
                        <th className="px-4 py-3 text-white font-semibold">Date / time</th>
                        <th className="px-4 py-3 text-white font-semibold">From</th>
                        <th className="px-4 py-3 text-white font-semibold">To</th>
                        <th className="px-4 py-3 text-white font-semibold">Changed by</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {shiftHistory.map((row) => (
                        <tr key={row.id} className="bg-white hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            {formatShiftHistoryDateTime(row.changed_at)}
                          </td>
                          <td className="px-4 py-3">{row.previous_shift_name || "—"}</td>
                          <td className="px-4 py-3">{row.new_shift_name || "—"}</td>
                          <td className="px-4 py-3">{row.changed_by || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {shiftHistoryTotalPages > 1 && (
                  <div className="mt-4 flex justify-center">
                    <Pagination
                      totalPages={shiftHistoryTotalPages}
                      currentPage={shiftHistoryPage}
                      onPageChange={(p) => fetchShiftHistory(p)}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

{/* 
      <div className='py-1 px-4 font-semibold w-full bg-gray-100'>Compensations</div>

      <div className='min-h-[200px] pt-2'>

      </div>

      <div className='py-1 px-4 font-semibold w-full bg-gray-100'>Benifits</div>

      <div className='min-h-[100px] pt-2'>

      </div> */}

      {/* Admin Edit Button
      {hasAdminPrivileges(userProfile?.role) && (
        <div className="mt-6 text-right">
          <button
            onClick={() => handleEditEmployee(employee)}
            className="flex gap-4 items-center px-4 pb-2 text-sm bg-[#674936] hover:bg-[#1a365d] text-white rounded"
          >
            Edit
          </button>
        </div>
      )} */}

      {/* Attendance Details Modal */}
      {showImageModal && selectedRecord && (
        // <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        //   <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full mx-4 mb-[18px] max-h-[90vh] overflow-y-auto">
        //     <div className="flex justify-between items-center mb-6">
        //       <h2 className="text-2xl font-bold text-gray-800">Attendance Details</h2>
        //       <button
        //         onClick={closeImageModal}
        //         className="text-gray-400 hover:text-gray-600 text-2xl"
        //       >
        //         ×
        //       </button>
        //     </div>

        //     {/* Attendance Information */}
        //     <div className="mb-6">
        //       <h3 className="text-lg font-semibold text-gray-800 mb-4">Attendance Information</h3>
        //       <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        //         <div>
        //           <h3 className="font-semibold text-gray-700 mb-2">Location</h3>
        //           <p className="text-gray-900">{selectedRecord.location_name}</p>
        //         </div>
        //         <div>
        //           <h3 className="font-semibold text-gray-700 mb-2">Date</h3>
        //           <p className="text-gray-900">{selectedRecord.work_date}</p>
        //         </div>
        //         <div>
        //           <h3 className="font-semibold text-gray-700 mb-2">Check In Time</h3>
        //           <p className="text-gray-900">{selectedRecord.check_in_time_str || '-'}</p>
        //         </div>
        //         <div>
        //           <h3 className="font-semibold text-gray-700 mb-2">Check Out Time</h3>
        //           <p className="text-gray-900">{selectedRecord.check_out_time_str || '-'}</p>
        //         </div>
        //         <div>
        //           <h3 className="font-semibold text-gray-700 mb-2">Duration</h3>
        //           <p className="text-gray-900">{selectedRecord.total_hours_formatted || '-'}</p>
        //         </div>
        //         <div>
        //           <h3 className="font-semibold text-gray-700 mb-2">Status</h3>
        //           <span
        //             className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
        //               selectedRecord.status === 'checked_in'
        //                 ? 'bg-green-100 text-green-700'
        //                 : 'bg-red-100 text-red-700'
        //             }`}
        //           >
        //             <span className="w-2 h-2 mr-1 rounded-full bg-current"></span>
        //             {selectedRecord.status === 'checked_in' ? 'Checked In' : 'Checked Out'}
        //           </span>
        //         </div>
        //       </div>
        //     </div>

        //     {/* Selfie Images */}
        //     <div className="mb-6">
        //       <h3 className="text-lg font-semibold text-gray-800 mb-4">Selfie Images</h3>
        //       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        //         {/* Check In Image */}
        //         {selectedRecord.image_url && (
        //           <div className="text-center">
        //             <h4 className="font-medium text-gray-600 mb-2">Check In Selfie</h4>
        //             <div className="relative">
        //               <img
        //                 src={selectedRecord.image_url}
        //                 alt="Check in selfie"
        //                 className="w-full h-48 object-cover rounded-lg shadow-lg mx-auto"
        //                 onError={(e) => {
        //                   e.target.style.display = 'none';
        //                   e.target.nextSibling.style.display = 'flex';
        //                 }}
        //               />
        //               <div
        //                 className="w-full h-48 bg-gray-100 rounded-lg shadow-lg mx-auto items-center justify-center text-gray-400 hidden"
        //               >
        //                 <div>
        //                   <div className="text-4xl mb-2">📷</div>
        //                   <p className="text-sm">Image not available</p>
        //                 </div>
        //               </div>
        //             </div>
        //           </div>
        //         )}

        //         {/* Check Out Image */}
        //         {selectedRecord.image_url_checkout && (
        //           <div className="text-center">
        //             <h4 className="font-medium text-gray-600 mb-2">Check Out Selfie</h4>
        //             <div className="relative">
        //               <img
        //                 src={selectedRecord.image_url_checkout}
        //                 alt="Check out selfie"
        //                 className="w-full h-48 object-cover rounded-lg shadow-lg mx-auto"
        //                 onError={(e) => {
        //                   e.target.style.display = 'none';
        //                   e.target.nextSibling.style.display = 'flex';
        //                 }}
        //               />
        //               <div
        //                 className="w-full h-48 bg-gray-100 rounded-lg shadow-lg mx-auto items-center justify-center text-gray-400 hidden"
        //               >
        //                 <div>
        //                   <div className="text-4xl mb-2">📷</div>
        //                   <p className="text-sm">Image not available</p>
        //                 </div>
        //               </div>
        //             </div>
        //           </div>
        //         )}

        //         {/* No Images Message */}
        //         {!selectedRecord.image_url && !selectedRecord.image_url_checkout && (
        //           <div className="col-span-2 text-center py-8 text-gray-500">
        //             <div className="text-4xl mb-2">📷</div>
        //             <p>No selfie images available for this record</p>
        //           </div>
        //         )}
        //       </div>
        //     </div>

        //     {/* Close Button */}
        //     <div className="flex justify-center">
        //       <button
        //         onClick={closeImageModal}
        //         className="bg-[#674936] text-white px-6 py-2 rounded-lg hover:bg-[#503220] transition-colors duration-300 font-semibold"
        //       >
        //         Close
        //       </button>
        //     </div>
        //   </div>
        // </div>
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 overflow-y-auto overscroll-contain py-4 px-2 sm:px-4 sm:items-center sm:py-6">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 max-w-4xl w-full max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto overscroll-contain my-auto">
            <div className="flex justify-between items-start gap-3 mb-6 min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 break-words pr-2">Attendance Details</h2>
              <button
                onClick={closeImageModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            {/* Record Details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check In Location</h3>
                  <p className="text-gray-900">
                    {selectedRecord.check_in_location_name ||
                      selectedRecord.location_name ||
                      '-'}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check Out Location</h3>
                  <p className="text-gray-900">{selectedRecord.check_out_location_name || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check In Date</h3>
                  <p className="text-gray-900">
                    {formatUsDateFromIso(
                      selectedRecord.check_in_date || selectedRecord.work_date
                    ) ||
                      selectedRecord.check_in_date ||
                      selectedRecord.work_date ||
                      '-'}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check Out Date</h3>
                  <p className="text-gray-900">
                    {formatUsDateFromIso(selectedRecord.check_out_date) ||
                      selectedRecord.check_out_date ||
                      '-'}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check In Time</h3>
                  <p className="text-gray-900">{selectedRecord.check_in_time_str || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check Out Time</h3>
                  <p className="text-gray-900">{selectedRecord.check_out_time_str || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Duration</h3>
                  <p className="text-gray-900">{selectedRecord.total_hours_formatted || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Status</h3>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getAttendanceStatusClass(
                      selectedRecord.status
                    )}`}
                  >
                    <span className="w-2 h-2 mr-1 rounded-full bg-current"></span>
                    {getAttendanceStatusLabel(selectedRecord.status)}
                  </span>
                </div>
                <div className="md:col-span-2">
                  <h3 className="font-semibold text-gray-700 mb-2">Check In Reason</h3>
                  <p className="text-gray-900">{selectedRecord.check_in_no_location_reason || '-'}</p>
                </div>
                <div className="md:col-span-2">
                  <h3 className="font-semibold text-gray-700 mb-2">Check Out Reason</h3>
                  <p className="text-gray-900">{selectedRecord.check_out_no_location_reason || '-'}</p>
                </div>
              </div>
            </div>

            {/* Images Section */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-700 mb-4">Selfie Images</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Check In Image */}
                {selectedRecord.image_url && (
                  <div className="text-center">
                    <h4 className="font-medium text-gray-600 mb-2">Check In Selfie</h4>
                    <div className="relative">
                      <img
                        src={selectedRecord.image_url}
                        alt="Check in selfie"
                        className="w-full h-48 object-cover rounded-lg shadow-lg mx-auto"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div 
                        className="hidden items-center justify-center h-48 bg-gray-100 rounded-lg text-gray-500"
                        style={{ display: 'none' }}
                      >
                        <div className="text-center">
                          <div className="text-4xl mb-2">📷</div>
                          <p>Image could not be loaded</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Check Out Image */}
                {selectedRecord.image_url_checkout && (
                  <div className="text-center">
                    <h4 className="font-medium text-gray-600 mb-2">Check Out Selfie</h4>
                    <div className="relative">
                      <img
                        src={selectedRecord.image_url_checkout}
                        alt="Check out selfie"
                        className="w-full h-48 object-cover rounded-lg shadow-lg mx-auto"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div 
                        className="hidden items-center justify-center h-48 bg-gray-100 rounded-lg text-gray-500"
                        style={{ display: 'none' }}
                      >
                        <div className="text-center">
                          <div className="text-4xl mb-2">📷</div>
                          <p>Image could not be loaded</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* No Images Message */}
                {!selectedRecord.image_url && !selectedRecord.image_url_checkout && (
                  <div className="col-span-2 text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">📷</div>
                    <p>No selfie images available for this record</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-center">
              <button
                onClick={closeImageModal}
                className="bg-[#674936] text-white px-6 py-2 rounded-lg hover:bg-[#503220] transition-colors duration-300 font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
